import { ReactNode } from "react";
import { toast } from "react-toastify";
import moment from "moment";
import { HiClock, HiExclamation, HiInformationCircle } from "react-icons/hi";
import { CustomButton } from "../atoms/CustomButton";
import { parsePayModDate, PlanChange } from "../../services/planChange";
import {
  useCancelPlanChange,
  useResumeSubscription,
} from "../../hooks/billing/planChange";
import { isEndingAtPeriodEnd, PlanStatusEntry } from "./planCatalog";
import { usePlanChangeFormat } from "./usePlanChangeFormat";

const TONES = {
  info: {
    box: "border-blue-200 bg-blue-50 text-blue-900",
    icon: "text-blue-600",
    Icon: HiInformationCircle,
  },
  warning: {
    box: "border-amber-200 bg-amber-50 text-amber-800",
    icon: "text-amber-600",
    Icon: HiExclamation,
  },
  pending: {
    box: "border-amber-200 bg-amber-50 text-amber-800",
    icon: "text-amber-600",
    Icon: HiClock,
  },
} as const;

const Banner = ({
  tone,
  text,
  actions,
}: {
  tone: keyof typeof TONES;
  text: string;
  actions?: ReactNode;
}) => {
  const { box, icon, Icon } = TONES[tone];
  return (
    <div className={`w-full rounded-lg border p-3 ${box}`}>
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="flex min-w-0 flex-1 items-start gap-2 sm:basis-64">
          <Icon className={`mt-0.5 shrink-0 ${icon}`} size={18} />
          <p className="text-sm">{text}</p>
        </div>
        {actions && (
          <div className="flex flex-wrap items-center gap-2">{actions}</div>
        )}
      </div>
    </div>
  );
};

/** Until the eve of the date: the day itself is too late. */
const beforeDay = (value: string | null | undefined) =>
  parsePayModDate(value).isValid() &&
  moment().isBefore(parsePayModDate(value).startOf("day"));

const PlanChangeBanners = ({
  customerId,
  entityId,
  planList,
  changes,
}: {
  customerId: string;
  entityId: string;
  planList: PlanStatusEntry[] | undefined | null;
  changes: PlanChange[] | undefined;
}) => {
  const { k, date, planName } = usePlanChangeFormat();
  const {
    mutate: cancelChange,
    isLoading: isCanceling,
    variables: cancelVariables,
  } = useCancelPlanChange();
  const {
    mutate: resume,
    isLoading: isResuming,
    variables: resumeVariables,
  } = useResumeSubscription();

  const pending = (changes || []).filter(
    (change) => change.status === "PENDING_PAYMENT"
  );
  const scheduled = (changes || []).filter(
    (change) => change.status === "SCHEDULED"
  );
  const ending = (planList || []).filter(isEndingAtPeriodEnd);

  if (pending.length === 0 && scheduled.length === 0 && ending.length === 0)
    return null;

  const cancel = (change: PlanChange, successKey: string) =>
    cancelChange(
      { customerId, entityId, id: change.plan_change_identification },
      {
        onSuccess: () => {
          toast.success(k(successKey));
        },
      }
    );

  const isCancelingThis = (change: PlanChange) =>
    isCanceling && cancelVariables?.id === change.plan_change_identification;

  return (
    <div className="flex w-full flex-col gap-3">
      {pending.map((change) => (
        <Banner
          key={change.plan_change_identification}
          tone="pending"
          text={k("banners.pending", { plan: planName(change.to_plan_code) })}
          actions={
            <CustomButton
              variant="secondary"
              size="sm"
              title={k("banners.abandon")}
              onClick={() => cancel(change, "toasts.abandoned")}
              isLoading={isCancelingThis(change)}
              disabled={isCanceling}
            />
          }
        />
      ))}

      {scheduled.map((change) => (
        <Banner
          key={change.plan_change_identification}
          tone="info"
          text={k("banners.scheduled", {
            plan: planName(change.to_plan_code),
            date: date(change.effective_at),
          })}
          actions={
            beforeDay(change.effective_at) && (
              <CustomButton
                variant="secondary"
                size="sm"
                title={k("banners.cancelScheduled")}
                onClick={() => cancel(change, "toasts.scheduledCanceled")}
                isLoading={isCancelingThis(change)}
                disabled={isCanceling}
              />
            )
          }
        />
      ))}

      {ending.map((entry) => (
        <Banner
          key={entry.plan}
          tone="warning"
          text={
            date(entry.current_period_end)
              ? k("banners.ending", {
                  plan: planName(entry.plan),
                  date: date(entry.current_period_end),
                })
              : k("banners.endingNoDate", { plan: planName(entry.plan) })
          }
          actions={
            (!entry.current_period_end ||
              moment().isBefore(parsePayModDate(entry.current_period_end))) && (
              <CustomButton
                variant="primary"
                size="sm"
                title={k("banners.resume")}
                onClick={() =>
                  resume(
                    { customerId, entityId, plan: entry.plan },
                    {
                      onSuccess: () => {
                        toast.success(k("toasts.resumed"));
                      },
                    }
                  )
                }
                isLoading={isResuming && resumeVariables?.plan === entry.plan}
                disabled={isResuming}
              />
            )
          }
        />
      ))}
    </div>
  );
};

export default PlanChangeBanners;
